import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-14fedd08"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "alert alert-light alert-sm"
}
const _hoisted_2 = { class: "instructions" }
const _hoisted_3 = { class: "list-unstyled profile-list" }
const _hoisted_4 = { class: "form-check" }
const _hoisted_5 = ["checked", "disabled", "onChange"]
const _hoisted_6 = { class: "add-row" }

import { computed, nextTick, ref } from 'vue'
import { PersonSet } from '@/rd/ResearchDataModel'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ProfileUtils } from './profiles/ProfileUtils'
import { CompositeId } from '@/rd/CompositeId'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import ProfileCard from './ProfileCard.vue'
import Modal from '@/util/Modal.vue'
import ProfileSelector from './profiles/ProfileSelector.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'PersonSetModal',
  props: {
    personId: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;



__expose({
  open,
})

const personSetRef = ref<PersonSet>()
const ownerIdRef = ref<string>()
const addedIdsRef = ref<string[]>([])
const selectedIdsRef = ref<string[]>([])
const showAddInputRef = ref(false)

const modalRef = ref<InstanceType<typeof Modal>>()
const selectorRef = ref<InstanceType<typeof ProfileSelector>>()

const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()

// all data should be loaded by parent
const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const ownerGroupRef = computed(() => dataGroupStore.getLoadedGroup(ownerGroupIdRef.value))

const isMatchOwnerRef = computed(() => CompositeId.isCompositeId(ownerIdRef.value))
const ownerGroupIdRef = computed(() => isMatchOwnerRef.value ? CompositeId.getGroupId(ownerIdRef.value!) : ownerIdRef.value)
const allIdsRef = computed(() => [...(viewPersonRef.value?.matchIds ?? []), ...addedIdsRef.value])
const profileIdsRef = computed(() => {
  const dataGroups = dataGroupStore.getLoadedGroupList(allIdsRef.value.map(id => CompositeId.getGroupId(id)))
  return ProfileUtils.getNonPlaceholderIds(allIdsRef.value, dataGroups)
    .map(id => CompositeId.parse(id))
})

const sourceTypeRef = computed(() => ownerGroupRef.value?.isProfile ? 'profile' : 'tree')
const hasPlaceholderRef = computed(() => ownerGroupIdRef.value && personSetRef.value?.memberIds.some(id => 
  CompositeId.hasGroupId(id, ownerGroupIdRef.value!) && ProfileUtils.isPlaceholder(id, ownerGroupRef.value!)))

const hasChangesRef = computed(() => !personSetRef.value ||
  selectedIdsRef.value.length != personSetRef.value.memberIds.length ||
  selectedIdsRef.value.some(id => !personSetRef.value!.memberIds.includes(id)))
const isValidSetRef = computed(() => !hasPlaceholderRef.value || selectedIdsRef.value.length > 1)
const canSaveChangesRef = computed(() => hasChangesRef.value && isValidSetRef.value)

function open(set: PersonSet | undefined, ownerId?: string) {
  if (!set && !ownerId)
    throw new Error('PersonSetModal: must provide either a set or owner')

  ownerIdRef.value = ownerId

  if (set) {
    if (ownerId) {
      if (set.groupId != ownerGroupIdRef.value)
        throw new Error(`PersonSetModal: owner ${ownerId} does not match set ${set.id}`)
    }
    else {
      ownerIdRef.value = set.groupId // implied
    }
  }
  else {
    // NOTE: it's possible for the specified owner to have multiple existing sets that include one or more matches
    // for the specified person, which if no set is specified, would create ambiguity about which set we're trying 
    // to modify. However, any set that is not a singleton should be displayed by the parent and passed to this modal
    // as the one being modified. So if no set is specified, then all existing sets must be singletons. And in that 
    // case, it doesn't matter which set we modify, because none of them currently have any effect.
    set = viewPersonStore.getLoadedPersonSets(props.personId)
      .find(s => s.groupId == ownerGroupIdRef.value) // choose first one with specified owner
  }

  personSetRef.value = set
  addedIdsRef.value = []
  selectedIdsRef.value = [...(set?.memberIds ?? [])] // make a copy!
  showAddInputRef.value = false

  modalRef.value?.open()
}

function canRemove(id: CompositeId) {
  return !(isMatchOwnerRef.value && id.value == ownerIdRef.value);
}

function toggleSelected(id: CompositeId) {
  const index = selectedIdsRef.value.indexOf(id.value)
  if (index >= 0) {
    selectedIdsRef.value.splice(index, 1)
  } else {
    selectedIdsRef.value.push(id.value)
  }
}

async function showAddInput() {
  showAddInputRef.value = true
  await nextTick()
  selectorRef.value?.focus()
}

function addProfile(id: string) {
  if (id && !profileIdsRef.value.find(pid => pid.value == id)) {
    addedIdsRef.value.push(id)
  }  
  showAddInputRef.value = false
}

async function saveChanges() {
  if (personSetRef.value) {
    const newMemberIds = selectedIdsRef.value
    const removedMemberIds = personSetRef.value.memberIds.filter(id => !newMemberIds.includes(id))
    await viewPersonStore.definePersonSetsAsync([newMemberIds, ...removedMemberIds.map(id => [id])], ownerGroupIdRef.value!)
  }
  else {
    // new set
  }
  modalRef.value?.close()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    ref_key: "modalRef",
    ref: modalRef,
    class: "person-set-modal",
    "ok-disabled": !canSaveChangesRef.value,
    onOk: saveChanges
  }, {
    title: _withCtx(() => [
      _createTextVNode(" Define matching ")
    ]),
    default: _withCtx(() => [
      (hasPlaceholderRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, " This source defines a relationship to the selected profiles. "))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, " Select the profiles this " + _toDisplayString(sourceTypeRef.value) + " considers to be matches. ", 1),
      _createElementVNode("ul", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(profileIdsRef.value, (id) => {
          return (_openBlock(), _createElementBlock("li", {
            key: id.value
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("input", {
                type: "checkbox",
                class: "form-check-input",
                checked: selectedIdsRef.value.includes(id.value),
                disabled: !canRemove(id),
                onChange: ($event: any) => (toggleSelected(id))
              }, null, 40, _hoisted_5)
            ]),
            _createVNode(ProfileCard, {
              personId: id.value,
              small: "",
              "show-profile-type": "",
              "this-profile": id.value == _ctx.personId,
              "select-mode": canRemove(id),
              static: !canRemove(id),
              onClick: ($event: any) => (toggleSelected(id))
            }, null, 8, ["personId", "this-profile", "select-mode", "static", "onClick"])
          ]))
        }), 128)),
        _createElementVNode("li", _hoisted_6, [
          (showAddInputRef.value)
            ? (_openBlock(), _createBlock(ProfileSelector, {
                key: 0,
                ref_key: "selectorRef",
                ref: selectorRef,
                "model-value": undefined,
                "onUpdate:modelValue": addProfile
              }, null, 512))
            : (_openBlock(), _createElementBlock("button", {
                key: 1,
                type: "button",
                class: "btn btn-inline btn-link",
                onClick: showAddInput
              }, " Add a profile "))
        ])
      ])
    ]),
    _: 1
  }, 8, ["ok-disabled"]))
}
}

})